import {Utils} from "../Utils";

export abstract class GenericApi {
    protected baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    protected async postAbsoluteWithParams<T = any>(base: string, params: any): Promise<T> {
        try {
            return await Utils.post<T>(base, {
                parameters: params
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async fetchAbsolute<T = any>(baseUrl: string): Promise<T> {
        try {
            return await Utils.fetch<T>(baseUrl);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    async saveFormData(url: string, data: FormData) : Promise<any> {
        return Utils.post(url, {
            body: data,
        });
    }

    protected async postAbsolute<T = any>(baseUrl: string, data: any): Promise<T> {
        try {
            return await Utils.post<T>(baseUrl, {
                body: data
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async putAbsolute<T = any>(baseUrl: string, data: any): Promise<T> {
        try {
            return await Utils.put<T>(baseUrl, {
                body: data
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async deleteAbsolute(baseUrl: string): Promise<void> {
        try {
            await Utils.delete(baseUrl);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async fetchData<T>(endpoint: string): Promise<any> {
        try {
            return await Utils.fetch<T>(`${this.baseUrl}/${endpoint}`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async fetchText(endpoint: string): Promise<string> {
        try {
            return await Utils.fetch<string>(`${this.baseUrl}/${endpoint}`, {
                type: 'text',
                headers: new Headers({ 'Accept': 'text/html' })
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async putData<T>(endpoint: string, data?: T): Promise<T> {
        try{
            return await Utils.put<T>(`${this.baseUrl}/${endpoint}`, {
                body: data
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async postData<T>(endpoint: string, data: any): Promise<T> {
        try {
            return await Utils.post<T>(`${this.baseUrl}${(endpoint !== '' && endpoint !== '/') ? '/' : ''}${endpoint}`, {
                body: data
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /** POST with query-string parameters (no JSON body) — for @RequestParam endpoints. */
    protected async postWithParams<T>(endpoint: string, params: Record<string, string | number | boolean>): Promise<T> {
        try {
            return await Utils.post<T>(`${this.baseUrl}${(endpoint !== '' && endpoint !== '/') ? '/' : ''}${endpoint}`, {
                parameters: params
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async getWithParams<T>(endpoint: string, params: Record<string, string | number | boolean>): Promise<T> {
        try {
            return await Utils.fetch<T>(`${this.baseUrl}${(endpoint !== '' && endpoint !== '/') ? '/' : ''}${endpoint}`, {
                parameters: params
            });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async deleteData<T>(endpoint: string): Promise<void> {
        try{
            return await Utils.delete(`${this.baseUrl}/${endpoint}`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    /**
     * DELETE whose response body matters — {@link deleteData} discards it.
     *
     * <p>Some deletes report what they did rather than just succeeding (how many rows came loose,
     * which ones were refused), and that has to reach the caller.
     */
    protected async deleteForResult<T>(endpoint: string): Promise<T> {
        try {
            return await Utils.delete(`${this.baseUrl}/${endpoint}`);
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async deleteWithBody<T>(endpoint: string, body: object): Promise<void> {
        try {
            return await Utils.delete(`${this.baseUrl}/${endpoint}`, { body });
        } catch (error) {
            console.error(error);
            throw error;
        }
    }

    protected async fetchFile(url: string) : Promise<any> {
        return Utils.fetch(url, {
            type: 'file',
            cache: 'no-store'
        });
    }
}